/**
 * THE WORKSPACE'S ROUTER — the browser's own history, and no dependency.
 *
 * WHY PATHS AND NOT HASHES. A destination is a place the Owner can be sent back
 * to: a notification's deep link, a reference chip, a bookmark on a phone.
 * `#/evidence/adj:…` and `/evidence/adj:…` both work, but only the second is a
 * URL a person would read, and the Worker already serves the shell for any
 * unmatched path. So the router uses real paths and the history API.
 *
 * THIS FILE IS ONLY THE PART THAT TOUCHES THE BROWSER. The URL grammar itself
 * lives in `paths.ts`, where it is a pure function of a string and can be
 * proved without a DOM — which matters, because the grammar is what a deep link
 * depends on and it should not need a browser to check.
 *
 * IT IS DELIBERATELY TINY. There is one Owner and ten destinations; a routing
 * library would be more code than the thing it routes. What a reference needs
 * is not pattern-matching sophistication but STABILITY of the segment order.
 */

import { signal } from '@preact/signals';
import { parsePath, pathFor, type Route } from './paths';

export type { Route } from './paths';
export { parsePath, pathFor } from './paths';

/** THE CURRENT ROUTE, AS A SIGNAL. Views read it; nothing writes it directly —
 *  `navigate` is the only writer, so the URL and the rendered view cannot
 *  disagree. */
export const route = signal<Route>(parsePath(window.location.pathname));

function sync(): void {
	route.value = parsePath(window.location.pathname);
}

/**
 * Move to a destination. `replace` is for the opening redirect, so the back
 * button does not walk into it.
 */
export function navigate(next: Route, options: { replace?: boolean } = {}): void {
	const path = pathFor(next);
	if (path === window.location.pathname) return;
	if (options.replace === true) window.history.replaceState(null, '', path);
	else window.history.pushState(null, '', path);
	sync();
}

/** Begin listening to the browser's own navigation. Returns the unsubscribe. */
export function startRouter(): () => void {
	window.addEventListener('popstate', sync);
	sync();
	return () => window.removeEventListener('popstate', sync);
}
